import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

// Session Z48 — one-shot sample bursts so owners can audition a style before
// committing to it. Mirrors the live celebration code in Inbox.jsx.
const previewVariant = (v) => {
  try {
    if (v === "rain") {
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          confetti({
            particleCount: 4, startVelocity: 10, spread: 220,
            origin: { x: Math.random(), y: 0 }, scalar: 0.5,
            colors: ["#38bdf8", "#a78bfa", "#f472b6"],
          });
        }, i * 180);
      }
    } else if (v === "sparkle") {
      confetti({
        particleCount: 60, spread: 110, startVelocity: 18, ticks: 60,
        scalar: 0.4, shapes: ["star"],
        colors: ["#fde047", "#fbbf24", "#fde68a"],
        origin: { y: 0.5 },
      });
    } else {
      confetti({
        particleCount: 32, spread: 55, startVelocity: 28, scalar: 0.6,
        colors: ["#f59e0b", "#10b981", "#0d7a75"],
        origin: { y: 0.4 },
      });
    }
  } catch { /* best effort */ }
};

export default function UiPrefsCard() {
  const [muted, setMuted] = useState(false);
  const [variant, setVariant] = useState("burst");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/ui-prefs")
      .then((r) => {
        setMuted(!!r.data?.confetti_muted);
        setVariant(r.data?.confetti_variant || "burst");
      })
      .catch(() => {});
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/settings/ui-prefs", patch);
      setMuted(!!r.data.confetti_muted);
      setVariant(r.data.confetti_variant || "burst");
      toast.success("Celebration prefs saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-card p-6" data-testid="settings-ui-prefs-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="ns-label">Workspace UI</div>
          <div className="font-serif text-xl mt-1">Celebrations</div>
          <div className="text-xs text-slate-400 mt-1">
            Tiny confetti bursts fire on wins like promoting a WhatsApp template. Pick a style or mute them entirely.
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </div>
      </div>
      <label
        className="mt-5 inline-flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer select-none"
        data-testid="settings-ui-prefs-confetti-toggle"
      >
        <input
          type="checkbox"
          checked={muted}
          disabled={busy}
          onChange={(e) => save({ confetti_muted: e.target.checked })}
          className="accent-teal-700"
        />
        Mute celebrations
      </label>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {["burst", "rain", "sparkle"].map((v) => (
          <div key={v} className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={busy || muted}
              onClick={() => save({ confetti_variant: v })}
              data-testid={`settings-ui-prefs-variant-${v}`}
              className={`text-[11px] rounded-full px-3 py-1 border transition ${
                variant === v
                  ? "bg-teal-700 text-white border-teal-700"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-stone-50"
              } ${muted ? "opacity-40" : ""}`}
            >
              {v === "burst" ? "🎉 Burst" : v === "rain" ? "🌧 Rain" : "✨ Sparkle"}
            </button>
            <button
              type="button"
              disabled={muted}
              onClick={() => previewVariant(v)}
              data-testid={`settings-ui-prefs-preview-${v}`}
              title={`Preview the ${v} style`}
              className={`text-[10px] rounded-full border border-slate-200 bg-white text-slate-500 px-2 py-0.5 hover:bg-stone-50 transition ${
                muted ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              Preview
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
